import React, { useEffect, useRef, useState } from 'react';
import mapboxgl, { GeolocateControl, ScaleControl } from 'mapbox-gl';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import { supported, notSupportedReason } from '@mapbox/mapbox-gl-supported';

import clsx from '../clsx';
import { LatLng, ControlPosition, MeasurementUnits } from './types';
import { MapContext } from './useMap';
import { getBoundsFromLatLng } from './mapboxGeometry';
import { getAccessToken, languageMapper } from './utils';
import { MAP_STYLES } from './constants';
import PointsOfInterest, { PointsOfInterestProps } from './PointsOfInterest';
import styles from './Map.module.scss';
import 'mapbox-gl/dist/mapbox-gl.css';

/**
 * This element will be displayed when mapbox-gl is not supported and when
 * `notSupportedFallbackElement` prop is not set on the Map component.
 */
const NotSupportedFallbackElementDefault = () => (
  <div className={styles.notSupported}>
    {/* TODO: update once we have a better specification on how we can pass in translated content into Map component */}
    <p>Browser does not support Mapbox GL</p>
  </div>
);

export interface MapOptions extends Omit<mapboxgl.MapboxOptions, 'container'> {}

export type MapProps = {
  /**
   * Mapbox access token. If not provided, the token will be retrieved from process.env.MAPBOX_ACCESS_TOKEN
   */
  APIToken?: string;

  /**
   * Called when map is loaded.
   */
  mapboxRef?: (mapbox: mapboxgl.Map) => void;

  /**
   * Mapbox options
   */
  options?: MapOptions;

  /**
   * Control placement
   */
  controlPlacement?: ControlPosition;

  /**
   * Show geolocate control
   */
  geolocateControl?: boolean;

  /**
   * Should geolocate on the user's location on map load
   */
  shouldCenterOnGeolocate?: boolean;

  /**
   * Show compass control
   */
  compassControl?: boolean;

  /**
   * Show zoom control
   */
  zoomControl?: boolean;

  /**
   * Language for the map
   */
  language?: string;

  /**
   * Measurement units for the map
   */
  measurementUnits?: MeasurementUnits;

  /**
   * When passed in the map will be centered on the provided coordinates.
   */
  latLng?: LatLng[];

  /**
   * PointsOfInterest layer options
   */
  poiOptions?: Omit<PointsOfInterestProps, 'measurementUnits'>;

  /**
   * Called when mapbox-gl is not supported.
   */
  onNotSupported?: (message: string) => void;

  /**
   * This element will be displayed when mapbox-gl is not supported.
   */
  notSupportedFallbackElement?: React.ReactNode;

  children?: React.ReactNode;
  className?: string;
};

const Map = ({
  APIToken,
  latLng,
  options,
  children,
  className,
  controlPlacement = 'top-left',
  zoomControl = true,
  geolocateControl = false,
  compassControl = false,
  shouldCenterOnGeolocate = false,
  language,
  mapboxRef: setMapboxRefProp,
  onNotSupported,
  notSupportedFallbackElement = <NotSupportedFallbackElementDefault />,
  measurementUnits = 'metric',
  poiOptions
}: MapProps) => {
  const [isSupported, setIsSupported] = useState(true);
  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const scaleControlRef = useRef<ScaleControl | null>(null);

  useEffect(() => {
    if (scaleControlRef.current) {
      scaleControlRef.current.setUnit(measurementUnits);
    }
  }, [measurementUnits]);

  useEffect(() => {
    if (
      map &&
      // Make sure the style is loaded before trying to update the style otherwise
      // mapbox-gl will thrown an error
      // @ts-expect-error accessing private property
      map.style?._loaded &&
      map.getStyle() !== options?.style &&
      options?.style !== undefined
    ) {
      map.setStyle(options.style);
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.style]);

  useEffect(() => {
    // Check to see if the browser supports Mapbox GL
    if (!supported()) {
      // `onNotSupported` is an optional callback method that can be defined if the
      // rendering application wants to handle Mapbox GL not being supported in some
      // way i.e. logging an error to Sentry some other service or rendering some sort
      // of fallback component
      if (onNotSupported) {
        const reason = notSupportedReason();
        const onNotSupportedMessage = `Mapbox GL is not supported: ${reason}`;
        onNotSupported(onNotSupportedMessage);
      }
      setIsSupported(false);

      return undefined;
    }

    const token = getAccessToken(APIToken);
    if (token) {
      mapboxgl.accessToken = token;
    }

    // Only call setRTLTextPlugin when status is 'unavailable' (i.e. not requested or removed)
    // https://docs.mapbox.com/mapbox-gl-js/api/properties/#getrtltextpluginstatus
    if (mapboxgl.getRTLTextPluginStatus() === 'unavailable') {
      mapboxgl.setRTLTextPlugin(
        'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js',
        // IN mapbox-gl official example they pass in `null` here like this and it works,
        // while also looking at the code it is nill by default, however the types do not allow it.
        // Once the types are updated we should be able to remove this ts-ignore.
        // @ts-expect-error
        null,
        true // Lazy load the plugin, only loaded when the map first encounters Hebrew or Arabic text.
      );
    }

    const mapbox = new mapboxgl.Map({
      container: mapContainerRef.current as HTMLElement,
      style: MAP_STYLES.standard,
      maxZoom: 21,
      fitBoundsOptions: { padding: 24 },
      pitchWithRotate: false,
      dragRotate: false,
      renderWorldCopies: false,
      // @ts-expect-error TODO: remove once types are added to `@types/mapbox-gl`
      performanceMetricsCollection: false,
      ...(latLng &&
        latLng.length > 0 && { bounds: getBoundsFromLatLng(latLng) }),
      ...options
    });

    let geolocate: GeolocateControl | null = null;
    if (geolocateControl) {
      geolocate = new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        fitBoundsOptions: {
          maxZoom: 13
        },
        showUserLocation: true,
        showAccuracyCircle: true
      });
      mapbox.addControl(geolocate, controlPlacement);
    }

    scaleControlRef.current = new mapboxgl.ScaleControl({
      maxWidth: 100,
      unit: measurementUnits
    });
    mapbox.addControl(scaleControlRef.current);

    mapbox.addControl(
      new mapboxgl.NavigationControl({
        showCompass: compassControl,
        showZoom: zoomControl
      }),
      controlPlacement
    );

    // TODO: look into if there are better ways on doing this
    // 1) is this the best place to add this?
    // 2) should "language" be required prop form Map component?
    // 3) is there a way to make it easier to add controls to Map component?
    // 4) What if we want to add another MapboxLanguage for another source/layer,
    //    how will that work?
    // We need to add a language control for each source separately and the plugin
    // gracefully handles if the source does not exist in the eventually loaded
    // map.
    if (language) {
      const languageOptions = {
        defaultLanguage: languageMapper(language),
        supportedLanguages: [
          'en',
          'de',
          'es',
          'fr',
          'it',
          'ja',
          'nl',
          'pt',
          'ru',
          'zh',
          'zh-Hans',
          'zh-Hant-TW'
        ]
      };
      const poiLanguageControl = new MapboxLanguage({
        ...languageOptions,
        ...{ languageSource: 'pois' }
      });
      const streetsLanguageControl = new MapboxLanguage({
        ...languageOptions,
        ...{ languageSource: 'composite' }
      });
      mapbox.addControl(poiLanguageControl);
      mapbox.addControl(streetsLanguageControl);
    }

    mapbox.on('load', () => {
      setMap(mapbox);
      setMapboxRefProp?.(mapbox);

      if (geolocate && shouldCenterOnGeolocate) {
        geolocate.trigger();
      }
    });

    return () => {
      mapbox.remove();
    };
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={mapContainerRef} className={clsx(styles.container, className)}>
      {map && (
        <MapContext.Provider value={{ map }}>
          <PointsOfInterest
            measurementUnits={measurementUnits}
            {...poiOptions}
          />
          {children}
        </MapContext.Provider>
      )}
      {!isSupported && notSupportedFallbackElement}
    </div>
  );
};

export default Map;
